import { z } from "zod";

// ── Education ──

export const ProfileEducationSchema = z.object({
    school: z.string(),
    school_id: z.uuid().nullable().optional(),
    school_logo_url: z.string().nullable(),
    degree: z.string(),
    field_of_study: z.string().nullable(),
    description: z.string().nullable(),
    grade: z.string().nullable(),
    activities: z.string().nullable(),
    start_year: z.number().int().nullable(),
    start_month: z.number().int().nullable(),
    end_year: z.number().int().nullable(),
    end_month: z.number().int().nullable(),
    duration_years: z.number().int().nullable(),
});
export type ProfileEducation = z.infer<typeof ProfileEducationSchema>;

// ── Experience ──

export const ProfileExperienceSchema = z.object({
    company: z.string(),
    company_id: z.uuid().nullable().optional(),
    company_logo_url: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    location: z.string().nullable(),
    job_type: z.string().nullable(),
    skills: z.array(z.string()),
    start_year: z.number().int().nullable(),
    start_month: z.number().int().nullable(),
    end_year: z.number().int().nullable(),
    end_month: z.number().int().nullable(),
    duration: z.string().nullable(),
    is_current: z.boolean(),
});
export type ProfileExperience = z.infer<typeof ProfileExperienceSchema>;

// ── Language ──

export const ProfileLanguageSchema = z.object({
    name: z.string(),
    proficiency: z.string(),
});
export type ProfileLanguage = z.infer<typeof ProfileLanguageSchema>;

// ── Classification evidence ──

/** One item that raised a classification flag: an experience title, the headline or the industry. */
export const EvidenceItemSchema = z.object({
    source: z.enum(["experience", "headline", "industry"]),
    text: z.string(),
    company: z.string().nullable(),
    is_current: z.boolean().nullable(),
    start_year: z.number().int().nullable(),
    end_year: z.number().int().nullable(),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * Why each keyword flag is on, recomputed from the profile's current data with
 * the enrichment's own patterns. Only the profile detail carries it.
 */
export const ClassificationEvidenceSchema = z.object({
    is_ceo: z.array(EvidenceItemSchema),
    is_founder: z.array(EvidenceItemSchema),
    is_ex_ceo: z.array(EvidenceItemSchema),
    is_ex_founder: z.array(EvidenceItemSchema),
    has_real_estate_knowledge: z.array(EvidenceItemSchema),
    has_financial_knowledge: z.array(EvidenceItemSchema),
    /** Every investor title; the flag needs them at 2+ distinct companies. */
    is_multiple_investor: z.array(EvidenceItemSchema),
    has_experiences: z.boolean(),
});
export type ClassificationEvidence = z.infer<typeof ClassificationEvidenceSchema>;

// ── Profile Response ──

/** One exit of a profile (#709), as an imported file reported it. */
export const ProfileExitSchema = z.object({
    id: z.string(),
    /** As the file named it. */
    company_name: z.string().nullable(),
    /** The company's LinkedIn page, `https://www.linkedin.com/company/<slug>`. */
    company_linkedin_url: z.string().nullable(),
    /**
     * The Invyo company with that LinkedIn page, when it exists and is enriched;
     * resolved on the profile page only, null elsewhere.
     */
    company_id: z.string().nullable(),
    /** ISO date. */
    exit_date: z.string().nullable(),
    /**
     * How much of `exit_date` the file gave (#654): a `year` or `month` date is
     * stored on the 1st and must be shown as `2024` / `07/2024`, never as a day.
     */
    exit_date_precision: z.enum(["year", "month", "day"]).nullable(),
    exit_amount: z.number().nullable(),
    /** ISO 4217 code of `exit_amount` (#661); null when the file did not say which currency. */
    exit_currency: z.string().nullable(),
});
export type ProfileExit = z.infer<typeof ProfileExitSchema>;

export const ProfileResponseSchema = z.object({
    id: z.uuid(),
    // Resolved for display: the admin-edited value when there is one, otherwise
    // the originally collected one. The collected values stay available below.
    full_name: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    // What the collectors (scraper, extension, imports) actually reported. Always
    // present: equal to the resolved values while no admin edit exists.
    original_full_name: z.string(),
    original_first_name: z.string(),
    original_last_name: z.string(),
    // The reference name (#703): the admin-edited value, else the collected one;
    // what Invyo and every other licence see. Differs from first/last_name only
    // where the viewing licence spells the name its own way.
    reference_first_name: z.string(),
    reference_last_name: z.string(),
    /** Non-null iff an admin, or the viewing licence (#703), edited the names: the UI's "this was corrected" witness. */
    name_edited_at: z.string().nullable(),
    headline: z.string().nullable(),
    about: z.string().nullable(),
    profile_image_url: z.string().nullable(),
    linkedin_url: z.string(),
    linkedin_id: z.string().nullable(),
    public_id: z.string().nullable(),
    urn: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * How reliable `email` is (#556): `verified` | `unverified`, or null when
     * there is no email. Three states, not a boolean: "no email" and "an email
     * nobody checked" are different facts and the dot shows them differently.
     */
    email_status: z.string().nullable(),
    /** When an admin marked `email` verified. Cleared whenever the value changes. */
    email_verified_at: z.string().nullable(),
    phone: z.string().nullable(),
    phone_status: z.string().nullable(),
    phone_verified_at: z.string().nullable(),
    /** Liquidity events reported by a file (#511, #709), most recent first (no date last). */
    exits: z.array(ProfileExitSchema),
    job_title: z.string().nullable(),
    company: z.string().nullable(),
    company_logo_url: z.string().nullable(),
    company_linkedin_url: z.string().nullable(),
    company_industry: z.string().nullable(),
    company_employee_range: z.string().nullable(),
    company_employee_count: z.number().int().nullable(),
    company_domain: z.string().nullable(),
    company_website: z.string().nullable(),
    company_description: z.string().nullable(),
    company_year_founded: z.number().int().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    continent: z.string().nullable(),
    state: z.string().nullable(),
    location: z.string().nullable(),
    hq_city: z.string().nullable(),
    hq_country: z.string().nullable(),
    hq_continent: z.string().nullable(),
    hq_region: z.string().nullable(),
    connection_count: z.number().int().nullable(),
    follower_count: z.number().int().nullable(),
    is_premium: z.boolean().nullable(),
    is_verified: z.boolean().nullable(),
    is_creator: z.boolean().nullable(),
    is_influencer: z.boolean().nullable(),
    school: z.string().nullable(),
    is_ceo: z.boolean().nullable(),
    is_founder: z.boolean().nullable(),
    is_ex_ceo: z.boolean().nullable(),
    is_ex_founder: z.boolean().nullable(),
    has_real_estate_knowledge: z.boolean().nullable(),
    has_financial_knowledge: z.boolean().nullable(),
    is_multiple_investor: z.boolean().nullable(),
    languages: z.array(ProfileLanguageSchema).nullable(),
    current_company_join_year: z.number().int().nullable(),
    current_company_join_month: z.number().int().nullable(),
    educations: z.array(ProfileEducationSchema).nullable(),
    experiences: z.array(ProfileExperienceSchema).nullable(),
    types: z.array(z.string()),
    /**
     * The ONE type the profile shows (#554): the winner of `types` under the
     * admin-set priority, or null when the profile carries none.
     *
     * `types` stays in the DTO: the tables show the winner, the profile's detail
     * shows everything it matched, and the four filters still read the array.
     */
    primary_type: z.string().nullable(),
    custom_tags: z.array(z.string()),
    list_ids: z.array(z.uuid()).nullable(),
    /**
     * The lists holding the profile, restricted to the caller's licence for a
     * non-admin. Only the profile detail carries it.
     */
    lists: z.array(z.object({ id: z.uuid(), name: z.string() })).optional(),
    /** Only the profile detail carries it; null when there is nothing to classify. */
    classification_evidence: ClassificationEvidenceSchema.nullable().optional(),
    is_favorite: z.boolean(),
    common_network_count: z.number().int().optional(),
    data_updated_at: z.string(),
    deactivated_at: z.string().nullable(),
    deactivation_reason: z.string().nullable(),
    /**
     * Full name of the admin who deactivated the profile, or null when the
     * deactivation came from an automatic scan rather than a person.
     */
    deactivated_by_name: z.string().nullable(),
    /**
     * Non-null once enrichment has failed permanently on this profile (today
     * only "not_found"): the row is queued for permanent deletion. Orthogonal to
     * `deactivated_at`: a profile can be one, the other, or both.
     */
    enrichment_status: z.string().nullable(),
    /**
     * What the exclusion scan found: at most one entry per scanned column.
     * `deactivation_reason` says by which mechanism the profile was deactivated,
     * this says why. Null on a manual deactivation and on profiles deactivated
     * before the column existed.
     */
    exclusion_matches: z
        .array(z.object({ field: z.string(), word: z.string() }))
        .nullable(),
    /**
     * When the profile was last restored from the deactivated list. Null when it
     * never was, and cleared by every deactivation: a profile is never both.
     */
    reactivated_at: z.string().nullable(),
    /** Full name of the admin who restored it; null when `reactivated_at` is. */
    reactivated_by_name: z.string().nullable(),
    /** Whether an Invyo expert reviewed and validated the profile (#671). Everyone sees it. */
    is_expert_validated: z.boolean(),
    /**
     * When, and by which admin, the profile was validated. Admin-only: always
     * null for a client, even on a validated profile; read `is_expert_validated`.
     */
    expert_validated_at: z.string().nullable(),
    expert_validated_by_name: z.string().nullable(),
    /**
     * Locked from cleaning by an admin (#702): no exclusion scan, enrichment
     * check, dead-profile purge or duplicate merge removes it. Admin-only: always
     * false for a client.
     */
    is_cleaning_locked: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

// ── Common Network ──

export const ViaUserSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    profile_image_url: z.string().nullable(),
});
export type ViaUser = z.infer<typeof ViaUserSchema>;

export const CommonNetworkProfileSchema = ProfileResponseSchema.extend({
    via_users: z.array(ViaUserSchema),
});
export type CommonNetworkProfile = z.infer<typeof CommonNetworkProfileSchema>;

// ── Wrappers ──

export const ProfileListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(ProfileResponseSchema),
    pagination: z.object({
        total: z.number().int(),
        page: z.number().int(),
        limit: z.number().int(),
        total_pages: z.number().int(),
    }).optional(),
});
export type ProfileListResponse = z.infer<typeof ProfileListResponseSchema>;

// ── Deactivation ──

// `list`   → remove the profiles from `list_id` only (ProfileList join row deleted); profile stays active elsewhere.
// `global` → set Profile.deactivatedAt; profile hidden everywhere and shown on the Deactivated Profiles page.
export const DeactivateProfilesRequestSchema = z
    .object({
        profile_ids: z.array(z.uuid()).min(1),
        scope: z.enum(["global", "list"]),
        list_id: z.uuid().optional(),
    })
    .refine((v) => v.scope !== "list" || !!v.list_id, {
        message: "list_id is required when scope is 'list'",
        path: ["list_id"],
    });
export type DeactivateProfilesRequest = z.infer<typeof DeactivateProfilesRequestSchema>;

export const DeactivateProfilesResponseSchema = z.object({
    deactivated_count: z.number().int(),
    list_id: z.uuid().optional(),
    profile_count: z.number().int().optional(),
});
export type DeactivateProfilesResponse = z.infer<typeof DeactivateProfilesResponseSchema>;

export const RestoreProfilesRequestSchema = z.object({
    profile_ids: z.array(z.uuid()).min(1),
});
export type RestoreProfilesRequest = z.infer<typeof RestoreProfilesRequestSchema>;

// ── Expert validation (#671) ──

// `validated: true` stamps the profiles that are not validated yet (an earlier
// validation keeps its author and date); `false` clears the stamp.
export const SetExpertValidationRequestSchema = z.object({
    profile_ids: z.array(z.uuid()).min(1),
    validated: z.boolean(),
});
export type SetExpertValidationRequest = z.infer<typeof SetExpertValidationRequestSchema>;

export const SetExpertValidationResponseSchema = z.object({
    /** Profiles whose state actually changed. */
    updated_count: z.number().int(),
});
export type SetExpertValidationResponse = z.infer<typeof SetExpertValidationResponseSchema>;

// ── Cleaning lock (#702) ──

// `locked: true` takes the profiles out of every cleaning process (exclusion
// scan and enrichment check, dead-profile purge, duplicate merges); `false` puts
// them back in on the next run. Stored as `exclusion_bypassed`.
export const SetCleaningLockRequestSchema = z.object({
    profile_ids: z.array(z.uuid()).min(1),
    locked: z.boolean(),
});
export type SetCleaningLockRequest = z.infer<typeof SetCleaningLockRequestSchema>;

export const SetCleaningLockResponseSchema = z.object({
    /** Profiles whose state actually changed. */
    updated_count: z.number().int(),
});
export type SetCleaningLockResponse = z.infer<typeof SetCleaningLockResponseSchema>;

export const RestoreProfilesResponseSchema = z.object({
    restored_count: z.number().int(),
    /**
     * Of the restored rows, how many were scan/enrichment-deactivated and are now
     * immunised against future scans (`exclusion_bypassed = true`). Only the
     * global `POST /profiles/restore` returns it; the list-scoped
     * `POST /lists/:id/restore-profiles` shares this shape but never immunises.
     */
    immunised_count: z.number().int().optional(),
});
export type RestoreProfilesResponse = z.infer<typeof RestoreProfilesResponseSchema>;

// ── HNWIs category band (#553) ──

// The live size of each category of the band, for the licence the caller belongs
// to. One read for the five figures: `GET /profiles` answers `total` for ONE
// filter set, so five calls per page load would be five queries racing each
// other to describe the same moment.
//
// Counted on `types` (#554): a profile counts under EVERY category one of its
// types belongs to (a Business Angel who is also an Exit entrepreneur is in
// both figures) but once per category. The figures therefore do not sum to
// `all`, so the remainder (`other`) is counted server-side, never derived.
export const ProfileCategoryCountsResponseSchema = z.object({
    all: z.number().int(),
    exit_entrepreneurs: z.number().int(),
    business_angels: z.number().int(),
    family_offices: z.number().int(),
    c_levels: z.number().int(),
    /** Profiles with none of the categories' or Institutionals' types, or no type at all. */
    other: z.number().int(),
    /** Profiles with at least one Institutionals type, each counted once. */
    institutionals_total: z.number().int(),
    /**
     * The types filed under Institutionals on the profile types page (#629), in
     * priority order, each with the profiles that carry it. Read from
     * `profile_types`, so a type filed there shows in the HNWIs Type facet with no
     * deploy; one nobody carries yet is listed with 0.
     */
    institutionals: z.array(z.object({ name: z.string(), profile_count: z.number().int() })),
});
export type ProfileCategoryCountsResponse = z.infer<typeof ProfileCategoryCountsResponseSchema>;

// GET /profiles/countries (#615), the options of the HNWIs Country facet: the
// distinct, non-empty countries of the licence's active profiles, sorted.
export const LicenceCountriesResponseSchema = z.object({
    countries: z.array(z.string()),
});
export type LicenceCountriesResponse = z.infer<typeof LicenceCountriesResponseSchema>;

export const ProfileCategoryCountsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileCategoryCountsResponseSchema,
});
export type ProfileCategoryCountsDetailResponse = z.infer<
    typeof ProfileCategoryCountsDetailResponseSchema
>;

// ── Admin contact verification (#556) ──

// How an admin marks a contact `verified`. Admin-only, one contact at a time,
// and reversible: an admin who marked the wrong one sends `verified: false`.
//
// The only other writer of `verified` is the Surf email import, for an address
// Surf validated itself (#631). We run no MX check of our own; the timestamp
// records when the claim was made.
export const UpdateContactStatusRequestSchema = z.object({
    contact: z.enum(["email", "phone"]),
    verified: z.boolean(),
});
export type UpdateContactStatusRequest = z.infer<typeof UpdateContactStatusRequestSchema>;

// Narrow on purpose, like the identity edit's response: the caller merges these
// four fields into the row it already holds rather than re-reading a whole
// profile (whose `custom_tags` are licence-scoped; see below).
export const UpdateContactStatusResponseSchema = z.object({
    id: z.uuid(),
    email_status: z.string().nullable(),
    email_verified_at: z.string().nullable(),
    phone_status: z.string().nullable(),
    phone_verified_at: z.string().nullable(),
});
export type UpdateContactStatusResponse = z.infer<typeof UpdateContactStatusResponseSchema>;

// ── Email import from a Surf CSV export (#631) ──

// Counted per PROFILE, errors per ROW (`row` = line in the file, header = 1).
// `unchanged` is a profile whose email already was the one in the file: what a
// second import of the same file reports, instead of claiming it imported again.
// `without_email` counts ROWS Surf found no email for, not errors.
export const ImportListEmailsResponseSchema = z.object({
    imported: z.number().int(),
    unchanged: z.number().int(),
    not_found: z.number().int(),
    without_email: z.number().int(),
    errors: z.array(z.object({ row: z.number().int(), reason: z.string() })),
});
export type ImportListEmailsResponse = z.infer<typeof ImportListEmailsResponseSchema>;

// ── Admin identity edit ──

// The three fields an admin may correct. The full name is NOT one of them: it is
// recomposed server-side from the names, so the two can never disagree.
//
// There is deliberately no "reset" flag and no nullable field: submitting the
// originally collected value IS the reset: the backend clears the matching
// `edited_*` column. (`toJsonSchema` emits OpenAPI 3 `nullable`, which Fastify's
// draft-07 Ajv ignores, so a `null` in a body would be rejected anyway.)
export const UpdateProfileIdentityRequestSchema = z.object({
    first_name: z.string().trim().max(120),
    last_name: z.string().trim().max(120),
    linkedin_url: z.string().trim().min(1).max(500),
});
export type UpdateProfileIdentityRequest = z.infer<typeof UpdateProfileIdentityRequestSchema>;

// Deliberately narrower than ProfileResponse: a full profile view needs the
// lists/tags/userProfiles includes, and `custom_tags` are licence-scoped: an
// admin saving from a drawer opened in another licence's context would get that
// licence's tags back and paint them over the row. Hosts merge these fields and
// refresh their own list instead.
export const UpdateProfileIdentityResponseSchema = z.object({
    id: z.uuid(),
    full_name: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    linkedin_url: z.string(),
    original_full_name: z.string(),
    original_first_name: z.string(),
    original_last_name: z.string(),
    reference_first_name: z.string(),
    reference_last_name: z.string(),
    name_edited_at: z.string().nullable(),
});
export type UpdateProfileIdentityResponse = z.infer<typeof UpdateProfileIdentityResponseSchema>;

/**
 * `PUT /profiles/:id/licence-name`: the caller's licence's own spelling of the
 * name (#703), shown to every member of that licence and to no one else. A blank
 * field, or one equal to the reference name, falls back to the reference; both
 * blank clears the licence's spelling. No null (same draft-07 Ajv reason as above).
 */
export const UpdateProfileLicenceNameRequestSchema = z.object({
    first_name: z.string().trim().max(120),
    last_name: z.string().trim().max(120),
});
export type UpdateProfileLicenceNameRequest = z.infer<typeof UpdateProfileLicenceNameRequestSchema>;

/** The identity as the licence now sees it; same narrow shape as the admin edit's. */
export const ProfileIdentityDetailResponseSchema = z.object({
    success: z.boolean(),
    data: UpdateProfileIdentityResponseSchema,
});
export type ProfileIdentityDetailResponse = z.infer<typeof ProfileIdentityDetailResponseSchema>;

// Aggregates for the Deactivated Profiles page header (admin).
//
// There is deliberately no "reactivated" figure. Since #512 a restore stamps
// `reactivated_at`, but the next deactivation clears it again: the column is a
// state, not a journal, and a count of it would drift with every scan.
export const DeactivatedProfilesStatsResponseSchema = z.object({
    total: z.number().int(),
    deactivated_this_month: z.number().int(),
    /**
     * Same count for the previous calendar month, so the band can show a trend
     * instead of an isolated figure (#532). The percentage is NOT computed here:
     * a month at zero has no percentage, and how to say that is a display rule.
     */
    deactivated_last_month: z.number().int(),
    /** Mean `deactivatedAt - createdAt` in days; null when nothing is deactivated. */
    avg_days_tracked: z.number().nullable(),
    /** ISO timestamp of the most recent deactivation; null when there is none. */
    last_deactivated_at: z.string().nullable(),
    /**
     * ISO timestamp of the last recorded exclusion scan; null while none has run
     * since #532 started recording them.
     *
     * Not the same fact as `last_deactivated_at`, which is what the band used to
     * show under a "Last scan" label: a manual deactivation moves that one and not
     * this one, and the post-enrichment exclusion check moves neither.
     */
    last_scan_at: z.string().nullable(),
    /**
     * What the last recorded scan did: profiles it deactivated, and matching
     * profiles it skipped because an earlier restore immunised them. Both null
     * while no scan has been recorded, the same gap as `last_scan_at`, and read
     * together with it.
     *
     * They come from the very row `last_scan_at` is read from, so exposing them
     * costs no extra query. Zero is a legitimate answer here and means the scan
     * found nothing; null means there was no scan.
     */
    last_scan_deactivated_count: z.number().int().nullable(),
    last_scan_bypassed_count: z.number().int().nullable(),
    /**
     * How many active, non-immunised profiles that run examined, and how long it
     * took in milliseconds (#536).
     *
     * Null on three different grounds, all of them honest: no scan has been
     * recorded at all; the run predates the columns; or the blocklist was empty,
     * so the run short-circuited without examining anything. The card leaves the
     * metric out rather than showing a zero none of the three means.
     */
    last_scan_scanned_count: z.number().int().nullable(),
    last_scan_duration_ms: z.number().int().nullable(),
});
export type DeactivatedProfilesStatsResponse = z.infer<typeof DeactivatedProfilesStatsResponseSchema>;

// Platform-wide tracking figure shown in the "track this profile" confirmation
// (#519). A profile starred by several admins counts once: the automatic
// re-enrichment it triggers runs per profile, not per admin.
export const MonitoredProfilesStatsResponseSchema = z.object({
    monitored_profiles: z.number().int(),
});
export type MonitoredProfilesStatsResponse = z.infer<typeof MonitoredProfilesStatsResponseSchema>;

// ── Custom Tags ──

// Tags are scoped to a licence. `license_id` (optional, admin-only) overrides the
// caller's own licence to target the currently-viewed licence context.
export const AddProfileTagsRequestSchema = z.object({
    profile_ids: z.array(z.uuid()).min(1),
    tags: z.array(z.string().min(1).max(50)).min(1),
    license_id: z.uuid().optional(),
});
export type AddProfileTagsRequest = z.infer<typeof AddProfileTagsRequestSchema>;

export const AddProfileTagsResponseSchema = z.object({
    added_count: z.number().int(),
});
export type AddProfileTagsResponse = z.infer<typeof AddProfileTagsResponseSchema>;

export const RemoveProfileTagRequestSchema = z.object({
    tag: z.string().min(1).max(50),
    license_id: z.uuid().optional(),
});
export type RemoveProfileTagRequest = z.infer<typeof RemoveProfileTagRequestSchema>;

export const RemoveProfileTagResponseSchema = z.object({
    removed_count: z.number().int(),
});
export type RemoveProfileTagResponse = z.infer<typeof RemoveProfileTagResponseSchema>;

export const ProfileTagSuggestionsResponseSchema = z.object({
    tags: z.array(z.string()),
});
export type ProfileTagSuggestionsResponse = z.infer<typeof ProfileTagSuggestionsResponseSchema>;


/** The longest note a member may write on a profile (#676, per note since #743): same ceiling as a licence's internal notes (#605). */
export const PROFILE_NOTES_MAX_LENGTH = 20_000;

/** Who wrote a profile note (#676). */
export const ProfileNotesAuthorSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
});
export type ProfileNotesAuthor = z.infer<typeof ProfileNotesAuthorSchema>;

/**
 * One note of a licence on a profile (#743): plain text, line breaks kept,
 * shared by every member of that licence and never shown to another licence.
 */
export const ProfileNoteSchema = z.object({
    id: z.uuid(),
    content: z.string(),
    created_at: z.string(),
    /** When the note was last edited; null if it never was. */
    updated_at: z.string().nullable(),
    /** Who wrote it; null once that user is deleted. */
    author: ProfileNotesAuthorSchema.nullable(),
    /** Whether the caller may edit or delete it: only its author may. */
    can_edit: z.boolean(),
});
export type ProfileNote = z.infer<typeof ProfileNoteSchema>;

/** `GET /profiles/:id/notes`: the caller's licence's notes on a profile, newest first. */
export const ProfileNotesSchema = z.object({
    notes: z.array(ProfileNoteSchema),
});
export type ProfileNotes = z.infer<typeof ProfileNotesSchema>;

export const ProfileNotesDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileNotesSchema,
});
export type ProfileNotesDetailResponse = z.infer<typeof ProfileNotesDetailResponseSchema>;

/** `POST /profiles/:id/notes` and `PATCH /profiles/:id/notes/:noteId`: the note's text. Blank is a 400. */
export const ProfileNoteContentRequestSchema = z.object({
    content: z.string().max(PROFILE_NOTES_MAX_LENGTH),
});
export type ProfileNoteContentRequest = z.infer<typeof ProfileNoteContentRequestSchema>;

export const ProfileNoteDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileNoteSchema,
});
export type ProfileNoteDetailResponse = z.infer<typeof ProfileNoteDetailResponseSchema>;

/**
 * The `GET /profiles` columns a table can sort on (#710), named as the API
 * names them. A table that pages on the server has to sort there too: sorting
 * the 25 rows it holds orders one page, not the list.
 */
export const PROFILE_SORT_FIELDS = [
    "full_name",
    "types",
    "email",
    "phone",
    "job_title",
    "company",
    "connection_count",
    "city",
    "country",
    "continent",
    "company_industry",
    "company_employee_range",
    "school",
    "hq_city",
    "hq_country",
    "exit_date",
    "exit_amount",
    "data_updated_at",
    "created_at",
] as const;
export type ProfileSortField = (typeof PROFILE_SORT_FIELDS)[number];

export const ProfileSortSchema = z.object({
    by: z.enum(PROFILE_SORT_FIELDS),
    order: z.enum(["asc", "desc"]),
});
export type ProfileSort = z.infer<typeof ProfileSortSchema>;
